import { AxiosInstance, AxiosResponse } from "axios";
import { ISituationProfessionnellePresentation } from "../../containers/DossierLocation/SituationProfessionnellePage/helpers";
import {
  DossierPiecesId,
  DossierParticulier,
  DossierParticulierTypeCompteEnum,
  DossiersLocationsApiFactory,
  DossiersProfessionnelsApiFactory,
  SituationPersonnelleDossierParticulier,
  SituationProfessionnelle,
  SituationProfessionnelleDossierParticulier,
  Fichier,
} from "./swagger";
import { wrapError } from "../../helpers/utils";

export interface IApi {
  postSituationPersonnelle(params: {
    dossierLocationRef: string;
    situationPersonnelle: SituationPersonnelleDossierParticulier;
  }): Promise<SituationPersonnelleDossierParticulier>;
  putSituationPersonnelle(params: {
    dossierLocationRef: string;
    dossierPersonneRef: string;
    situationPersonnelle: SituationPersonnelleDossierParticulier;
  }): Promise<void>;
  getListSituationsProfessionnelles(): Promise<SituationProfessionnelle[]>;
  putSituationProfessionnelle(params: {
    dossierLocationRef: string;
    dossierParticulierRef: string;
    situationProfessionnelle: SituationProfessionnelleDossierParticulier;
  }): Promise<DossierPiecesId>;
  postDossierParticulierDocument(params: {
    dossierLocationRef: string;
    dossierParticulierRef: string;
    fichier: Fichier;
  }): Promise<number>;
}

const create = (endpoint: string, axiosClient: AxiosInstance) => {
  const dossierLocationApi = DossiersLocationsApiFactory(
    { isJsonMime: () => true },
    endpoint,
    axiosClient
  );

  const SituationProfessionnelleApi = DossiersProfessionnelsApiFactory(
    { isJsonMime: () => true },
    endpoint,
    axiosClient
  );

  /**
   * Enregistrer une situation personnelle
   * On créé element par element
   * Meme si l'API permet de faire une création en masse
   *
   * @param params : Critères de recherche
   */
  const postSituationPersonnelle = (params: {
    dossierLocationRef: string;
    situationPersonnelle: DossierParticulier;
  }): Promise<DossierParticulier> => {
    const promise: Promise<DossierParticulier> = new Promise(
      (resolve, reject) => {
        const situationPerso = {
          ...params.situationPersonnelle,
          typeCompte:
            params.situationPersonnelle.typeCompte ||
            DossierParticulierTypeCompteEnum.LOCATAIRE,
        };
        dossierLocationApi
          .dossiersLocationRefDossiersParticuliersPost(
            params.dossierLocationRef,
            situationPerso,
            {}
          )
          .then((response: AxiosResponse<DossierParticulier>) => {
            resolve(response.data);
          })
          .catch((error) => reject(wrapError(error)));
      }
    );
    return promise;
  };

  const putSituationPersonnelle = (params: {
    dossierLocationRef: string;
    dossierPersonneRef: string;
    situationPersonnelle: SituationPersonnelleDossierParticulier;
  }): Promise<void> => {
    const promise: Promise<void> = new Promise((resolve, reject) => {
      dossierLocationApi
        .dossiersLocationRefDossiersParticuliersRefPersSituationPersonnellePut(
          params.dossierLocationRef,
          params.dossierPersonneRef,
          params.situationPersonnelle,
          {}
        )
        .then(() => resolve())
        .catch((error) => reject(wrapError(error)));
    });
    return promise;
  };

  const getListSituationsProfessionnelles = (): Promise<
    SituationProfessionnelle[]
  > => {
    const promise: Promise<SituationProfessionnelle[]> = new Promise(
      (resolve, reject) => {
        SituationProfessionnelleApi.getSituationsProfessionnelles({})
          .then((response: AxiosResponse<SituationProfessionnelle[]>) =>
            resolve(response.data)
          )
          .catch((error) => reject(wrapError(error)));
      }
    );
    return promise;
  };

  const putSituationProfessionnelle = (params: {
    dossierLocationRef: string;
    dossierParticulierRef: string;
    situationProfessionnelle: ISituationProfessionnellePresentation;
  }): Promise<DossierPiecesId> => {
    const promise: Promise<DossierPiecesId> = new Promise((resolve, reject) => {
      dossierLocationApi
        .dossiersLocationRefDossiersParticuliersRefPersSituationProfessionnellePut(
          params.dossierLocationRef,
          params.dossierParticulierRef,
          params.situationProfessionnelle,
          {}
        )
        .then((response) => resolve(response.data))
        .catch((error) => reject(wrapError(error)));
    });
    return promise;
  };
  const postDossierParticulierDocument = (params: {
    dossierLocationRef: string;
    dossierParticulierRef: string;
    fichier: Fichier;
  }): Promise<number> => {
    const promise: Promise<number> = new Promise((resolve, reject) => {
      dossierLocationApi
        .postDossierParticulierDocument(
          params.dossierLocationRef,
          params.dossierParticulierRef,
          params.fichier,
          {}
        )
        .then((response) => resolve(response.status))
        .catch((error) => reject(wrapError(error)));
    });
    return promise;
  };

  return {
    postSituationPersonnelle,
    putSituationPersonnelle,
    getListSituationsProfessionnelles,
    putSituationProfessionnelle,
    postDossierParticulierDocument,
  };
};

export default create;
